/**
 * LandingMarketplaceComingSoon — gated placeholder for the parent
 * brand's domain before public launch.
 *
 * Renders whenever the brand context resolves to `marketplace` (either
 * via the future production hostname or the `?brand=marketplace` query
 * param). Replaces the full `LandingMarketplace` until the user opts
 * to take the page live.
 *
 * The full marketplace landing is still mounted at `/marketplace-preview`
 * (App.js) so the user can review their work in private without
 * leaking the new brand to the public.
 *
 * To FLIP THIS GATE OFF on launch day:
 *   1. Open `/app/frontend/src/App.js`
 *   2. Find the `<BrandLandingRouter ... marketplace={…} />` line
 *   3. Change `<LandingMarketplaceComingSoon />` back to `<LandingMarketplace />`
 *   4. Save — hot reload picks it up
 */
import React, { useEffect, useState } from 'react';
import { Sparkles, ArrowRight } from 'lucide-react';
import { useBrand } from '../brand/useBrand';
import { PALETTE_TOKENS } from '../brand/brandContext';
import SEO from '../components/SEO';

const NAVY = PALETTE_TOKENS.navy;
const PINE = PALETTE_TOKENS.pine;
const PINK = PALETTE_TOKENS.pink;
const OFFWHITE = PALETTE_TOKENS.offwhite;

const LandingMarketplaceComingSoon = () => {
  const { brand, sibling, crossBrandHref } = useBrand();
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  // Tiny optimistic notify-me list. Stored in localStorage for now —
  // when the parent brand goes live we wire this to /api/waitlist.
  const submit = (e) => {
    e.preventDefault();
    const clean = (email || '').trim();
    if (!clean.includes('@')) return;
    try {
      const existing = JSON.parse(localStorage.getItem('marketplace_waitlist') || '[]');
      if (!existing.includes(clean)) existing.push(clean);
      localStorage.setItem('marketplace_waitlist', JSON.stringify(existing));
    } catch { /* private mode — silently skip */ }
    setSubmitted(true);
  };

  // Lock body scroll on the gate so the curtain feels intentional.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, []);

  return (
    <div
      className="min-h-screen flex items-center justify-center px-5 py-10 relative overflow-hidden"
      style={{ background: OFFWHITE }}
      data-testid="landing-marketplace-coming-soon"
    >
      <SEO
        title={`${brand.displayName} — Coming Soon`}
        description={`${brand.displayName} is launching soon. Gift cards, experiences, and a Montreal travel concierge in one place.`}
      />

      {/* Top nav — even on the coming-soon gate we surface a host
          acquisition CTA. Every visitor here is a warm lead; hosts
          browsing the parent brand should have a one-click path into
          Homesbyli's host onboarding. Cross-brand href resolves to the
          stays domain in production and to `?brand=stays` on preview. */}
      <div className="absolute top-0 left-0 right-0 z-10 px-5 py-4 flex items-center justify-between">
        <div
          className="text-sm font-bold tracking-tight"
          style={{ color: NAVY }}
          data-testid="coming-soon-wordmark"
        >
          {brand.displayName}
        </div>
        <a
          href={crossBrandHref('/host-application', { ref: 'ucwonder-comingsoon' })}
          className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold rounded-full px-4 py-2 transition-opacity hover:opacity-90"
          style={{ background: PINE, color: '#fff' }}
          data-testid="coming-soon-join-us"
          title="List your Montreal home on Homesbyli"
        >
          Join Us
        </a>
      </div>

      {/* Soft ambient blobs in brand palette */}
      <div
        aria-hidden
        className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full opacity-30 blur-3xl pointer-events-none"
        style={{ background: PINE }}
      />
      <div
        aria-hidden
        className="absolute -bottom-32 -right-32 w-[420px] h-[420px] rounded-full opacity-25 blur-3xl pointer-events-none"
        style={{ background: PINK }}
      />

      <div className="relative max-w-2xl w-full text-center">
        <div
          className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em] mb-6 px-3 py-1.5 rounded-full"
          style={{ background: '#fff', color: NAVY, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
          data-testid="coming-soon-badge"
        >
          <Sparkles className="w-3.5 h-3.5" />
          Coming Soon
        </div>

        <h1
          className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-gray-900 leading-[1.02]"
          data-testid="coming-soon-headline"
        >
          {brand.displayName}
        </h1>
        <div
          className="mt-3 text-sm font-bold uppercase tracking-[0.28em]"
          style={{ color: PINE }}
          data-testid="coming-soon-tagline"
        >
          {brand.tagline}
        </div>
        <div
          className="mt-6 text-xs font-bold uppercase tracking-[0.28em]"
          style={{ color: PINK }}
          data-testid="coming-soon-caption"
        >
          Coming Soon
        </div>
        <p className="mt-5 text-base sm:text-lg text-gray-700 max-w-xl mx-auto leading-relaxed">
          Meet your <strong>AI travel concierge</strong>. Plan smarter trips,
          unlock curated stays, hand-picked local experiences, and exclusive
          perks across 3000+ brands — everything in one intelligent place.
        </p>

        {/* Notify-me form */}
        {submitted ? (
          <div
            className="mt-9 inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold"
            style={{ background: `${PINE}15`, color: PINE }}
            data-testid="coming-soon-submitted"
          >
            <Sparkles className="w-4 h-4" />
            Thanks — we&apos;ll let you know the moment it&apos;s live.
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="mt-9 flex flex-col sm:flex-row gap-2 max-w-md mx-auto"
            data-testid="coming-soon-form"
          >
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="flex-1 h-11 rounded-full px-5 text-sm bg-white ring-1 ring-gray-200 focus:outline-none focus:ring-2"
              style={{ '--tw-ring-color': PINE }}
              data-testid="coming-soon-email"
              aria-label="Email address"
            />
            <button
              type="submit"
              className="h-11 px-6 rounded-full text-sm font-semibold text-white inline-flex items-center justify-center gap-1.5 hover:opacity-90 transition-opacity"
              style={{ background: PINK }}
              data-testid="coming-soon-notify"
            >
              Notify me
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </form>
        )}

        {/* Cross-brand bridge — Homesbyli is live now, so always show
            the link out so visitors aren't completely stuck on a wall. */}
        {sibling && (
          <div className="mt-12 pt-8 border-t border-gray-200 max-w-md mx-auto">
            <div className="text-xs text-gray-500 uppercase tracking-[0.18em] font-semibold mb-2">
              In the meantime
            </div>
            <a
              href={crossBrandHref('/')}
              className="inline-flex items-center gap-1.5 text-sm font-semibold hover:underline"
              style={{ color: NAVY }}
              data-testid="coming-soon-cross-stays"
            >
              Browse {sibling.displayName} stays in Montreal
              <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default LandingMarketplaceComingSoon;
